import { useEffect, useId } from 'react'

import { Sparkles } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { LoadingSkeleton } from '@/components/LoadingSkeleton/LoadingSkeleton'

type AgentV2MatterArtifactEditLoadingReviewProps = {
  canCancel: boolean
  onCancel: () => void
}

/** Loading state for a selected-passage edit before its review proposal arrives. */
export default function AgentV2MatterArtifactEditLoadingReview({ canCancel, onCancel }: AgentV2MatterArtifactEditLoadingReviewProps) {
  const titleId = useId()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !canCancel) return
      event.preventDefault()
      onCancel()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canCancel, onCancel])

  return (
    <div
      aria-busy="true"
      aria-labelledby={titleId}
      className="pointer-events-auto flex max-h-full min-h-0 flex-col gap-6 rounded-2xl bg-brand-neutral-50 p-6 shadow-[0_4px_6px_0px_rgba(0,0,0,0.1),0_2px_4px_0px_rgba(0,0,0,0.06)]"
      data-agent-v2-artifact-edit-loading-review
    >
      <div className="flex min-h-0 flex-col gap-4">
        <div className="flex shrink-0 items-center gap-2">
          <Sparkles aria-hidden className="h-6 w-6 shrink-0 text-brand-neutral-950" />
          <h2 className="text-lg font-bold text-brand-neutral-950" id={titleId}>
            Applying edit...
          </h2>
        </div>

        <div className="flex flex-col gap-2">
          <LoadingSkeleton width="100%" />
          <LoadingSkeleton width="90%" />
          <LoadingSkeleton width="80%" />
          <LoadingSkeleton width="85%" />
        </div>
      </div>

      <div className="flex shrink-0 justify-end">
        <Button color="primary" disabled={!canCancel} onClick={onCancel} size="md" variant="outlined">
          Cancel
        </Button>
      </div>
    </div>
  )
}
